import * as React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slide,
  Typography,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { useParams } from 'react-router'
import { useGetSelectedCustomerData } from 'pages/selected-customer/selected-customer.controller'
import { handleUpdateRestrictions } from '../../restrictions.controller'
import { findPlanExceptions } from '../../plan-exceptions'
import Exception from './exception'
import { ExceptionType } from '../../restrictions.types'
import { useUpdateCustomer } from 'services/queries/use-update-customer'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

function RestoreDefaultsDialog() {
  const [open, setOpen] = React.useState(false)
  const { id: selectedCustomer } = useParams()
  const { data, isLoading } = useGetSelectedCustomerData(
    Number(selectedCustomer),
  )
  const { customerPlanData, plansData, customerData } = data ?? {}
  const { mutate: updateCustomer } = useUpdateCustomer()
  const currentPlan = customerPlanData?.subscription_plan

  const defaultValuesForPlan = plansData?.find(
    (plan) => plan.name === currentPlan,
  )
  // ENG2-2455 (T4): compare against the full template row; the customer's
  // own country resolves the MANUAL_PACKAGE placeholder.
  const exceptions = findPlanExceptions(
    customerPlanData,
    defaultValuesForPlan,
    customerData?.country,
  )

  const restoreException = (exception: ExceptionType) => {
    if (!selectedCustomer) return

    switch (exception.type) {
      case 'right':
        handleUpdateRestrictions({
          selectedCustomer,
          updateCustomer,
          newValue: null,
          element: {
            updateLocation: exception.isGlobal ? 'global' : undefined,
            key: exception.key,
          },
        })
        break
      case 'cap':
        handleUpdateRestrictions({
          selectedCustomer,
          updateCustomer,
          newValue: null,
          element: {
            key: exception.key,
          },
        })
        break
      case 'country':
        // Whole-country reset: RisikaAPI's merge_exceptions pops a country
        // sent as null. Sending { score_models: null } instead would be
        // stored as a null list for a customer without a prior exception.
        updateCustomer({
          companyId: selectedCustomer,
          reqBody: { data: { rights: { [exception.key]: null } } },
        })
        break

      default:
        break
    }
  }
  return (
    <>
      <Button
        color="primary"
        disabled={isLoading}
        variant="contained"
        onClick={() => setOpen(true)}
        fullWidth
        size="small"
      >
        Restore Defaults
      </Button>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Restore Defaults</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Field
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Current
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Default
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Action
              </Typography>
            </Grid>
            {exceptions.map((exception) => (
              <Exception
                key={exception.key}
                exception={exception}
                handleRestore={(exception) => restoreException(exception)}
              />
            ))}
            {exceptions.length === 0 && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  No customizations to restore. All values match plan defaults.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RestoreDefaultsDialog
